<template>
  <transition
    name="fade"
    mode="out-in"
    @beforeLeave="beforeLeave"
    @enter="enter"
    @afterEnter="afterEnter"
  >
    <section
      :id="id"
      ref="provider-selection"
      class="provider-selection"
      v-show="isShown"
    >
      <PaginationControls
        v-if="!isProviderSelected && searchedProviders.length > pageSize"
        :page-number="pageNumber"
        :page-size="pageSize"
        :total-items="searchedProviders.length"
        :page-count="pageCount"
        @prev-click="setPageNumber(pageNumber - 1)"
        @next-click="setPageNumber(pageNumber + 1)"
      ></PaginationControls>
      <p v-if="isProviderSelected" class="provider-selection_description">
        You’ve selected your provider. To select a different provider, start a
        new zip code search or
        <a @click="returnToResults">return to your provider results.</a>
      </p>
      <p v-else class="provider-selection_description">
        <span
          v-if="searchedProviders.length > 0"
          tabindex="-1"
          ref="description"
          >We found <strong>{{ searchedProviders.length }} providers</strong> in
          this area. Select your provider to continue applying.</span
        >
        <span tabindex="-1" ref="description" v-else
          >Sorry, we found
          <strong>{{ searchedProviders.length }} providers</strong> in this
          area. Please search a different zip code or reach out to your provider
          directly.</span
        >
      </p>
      <div v-if="isProviderSelected" class="provider-selection_selected">
        <ProviderSelectionItem
          :provider="selectedProvider"
          :selected="true"
        ></ProviderSelectionItem>
      </div>
      <div v-else class="provider-selection_list" role="list">
        <transition-group
          name="list"
          mode="out-in"
          @beforeLeave="beforeLeave"
          @enter="enter"
          @afterEnter="afterEnter"
        >
          <ProviderSelectionItem
            v-for="provider in paginatedSearchedProviders"
            :provider="provider"
            :key="`${provider.spid}${provider.location.locationID}`"
            :selected="isSelected(provider)"
            role="listitem"
            v-show="showProvider(provider)"
            @not-enrolled="handleNotEnrolled"
          ></ProviderSelectionItem>
        </transition-group>
      </div>
      <div
        v-if="!isProviderSelected && searchedProviders.length > 0"
        class="provider-selection_message"
      >
        If you don't see your provider, reach out to them directly.
      </div>
      <ProviderNotEnrolledModal
        :show="showNotEnrolledModal"
        :provider="notEnrolledModalProvider"
        @close="showNotEnrolledModal = false"
      ></ProviderNotEnrolledModal>
    </section>
  </transition>
</template>

<script>
import Vue from "vue";
import Uuid from "@Shared/utils/Uuid";
import { mapGetters, mapActions } from "vuex";
import ProviderSelectionItem from "./ProviderSelectionItem";
import { EventBus } from "@Shared/utils/event-bus";
import PaginationControls from "../PaginationControls";
import ProviderNotEnrolledModal from "./ProviderNotEnrolledModal";

export default {
  name: "ProviderSelection",
  components: {
    ProviderSelectionItem,
    PaginationControls,
    ProviderNotEnrolledModal,
  },
  data() {
    return {
      id: "vertical-progress-flow-" + Uuid(),
      stepIndex: 1, // 0 count, must match component order in slot
      showNotEnrolledModal: false,
      notEnrolledModalProvider: {},
    };
  },
  computed: {
    ...mapGetters([
      "enrollmentCurrentSearchStepIndex",
      "searchedProviders",
      "selectedProvider",
      "requestStatus",
      "paginatedSearchedProviders",
      "pageNumber",
      "pageSize",
    ]),

    isShown() {
      return this.stepIndex <= this.enrollmentCurrentSearchStepIndex;
    },
    isProviderSelected() {
      if (
        Object.keys(this.selectedProvider).length === 0 &&
        this.selectedProvider.constructor === Object
      ) {
        return false;
      } else {
        return true;
      }
    },
    pageCount() {
      return Math.ceil(this.searchedProviders.length / this.pageSize);
    },
  },
  watch: {
    isShown(val) {
      this.$nextTick(() => {
        // Vue.set(this.verticalProgressSteps[this.stepIndex], 'offsetTop', this.$refs["provider-selection"].offsetTop);
        this.updateOffsetTop();

        if (val) {
          this.$refs.description.focus();
        }
      });
    },
  },
  methods: {
    ...mapActions(["setPageNumber", "setPageSize", "resetSelectedProvider"]),
    returnToResults() {
      this.verticalProgressSteps.pop();
      this.resetSelectedProvider();
    },
    isSelected(provider) {
      if (
        Object.keys(this.selectedProvider).length === 0 &&
        this.selectedProvider.constructor === Object
      ) {
        return false;
      }
      return this.selectedProvider.spid === provider.spid;
    },
    showProvider(provider) {
      if (
        Object.keys(this.selectedProvider).length === 0 &&
        this.selectedProvider.constructor === Object
      ) {
        return true;
      } else {
        return this.isSelected(provider);
      }
    },
    updateOffsetTop() {
      Vue.set(
        this.verticalProgressSteps[this.stepIndex],
        "offsetTop",
        this.$refs["provider-selection"].offsetTop
      );
    },
    handleNotEnrolled(provider) {
      this.showNotEnrolledModal = true;
      this.notEnrolledModalProvider = provider;
      this.$ga.event({
        eventCategory: "Provider Not Enrolled",
        eventAction: this.notEnrolledModalProvider.providerName,
        eventLabel: this.notEnrolledModalProvider.spid,
      });
    },
    beforeLeave(element) {
      this.prevHeight = getComputedStyle(element).height;
    },
    enter(element) {
      const { height } = getComputedStyle(element);
      element.style.height = this.prevHeight;
      setTimeout(() => {
        element.style.height = height;
      });
    },
    afterEnter(element) {
      element.style.height = "auto";
    },
  },
  inject: ["verticalProgressSteps"],
  mounted() {
    this.verticalProgressSteps.push({
      id: this.id,
      stepIndex: this.stepIndex,
      offsetTop: this.$refs["provider-selection"].offsetTop,
    });
    EventBus.$on("window-resize", this.updateOffsetTop);
    EventBus.$on("update-offset", this.updateOffsetTop);
  },
  destroyed() {
    EventBus.$off("window-resize", this.updateOffsetTop);
    EventBus.$off("update-offset", this.updateOffsetTop);
  },
};
</script>

<style lang="scss" scoped>
.provider-selection {
  margin-top: 35px;
  position: relative;

  .provider-selection_description {
    font-family: "Open Sans";
    font-size: 18px;
    line-height: 24px;
    padding: 14px 150px 14px 0;

    a {
      cursor: pointer;
      color: var(--primary-ada-blue-100, #007da7);
      font-weight: 700;
    }

    @media (max-width: $grid-breakpoint-sm) {
      padding: 5px 20px 30px;
      font-size: 16px;
      line-height: 22px;
      text-align: center;
    }
  }
}

.pagination-controls {
  position: absolute;
  top: 10px;
  right: 0;

  @media (max-width: $grid-breakpoint-sm) {
    top: unset;
    left: 0;
    bottom: 100px;
    text-align: center;
  }
}

.provider-selection_message {
  margin-top: 30px;

  @media (max-width: $grid-breakpoint-sm) {
    padding-left: 20px;
    padding-right: 20px;
    margin-top: 90px;
    text-align: center;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition-duration: 0.5s;
  transition-property: height, opacity;
  transition-timing-function: ease;
  overflow: hidden;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}

.list-enter-active,
.list-leave-active {
  transition: all 0.5s;
}
.list-enter,
.list-leave-to {
  opacity: 0;
}
</style>
